/**
 * workspaceModel.js
 *
 * Pure (React-free, IO-free) model of the services-app **workspace dashboard**:
 * an ordered list of workspaces (tabs), each holding an ordered list of widget
 * instances. Everything here is a plain function over plain data so the whole
 * rule set is assertable from Jest (same split as `helpers/dashboardPreferences.js`).
 * Widget-level operations live in `workspaceWidgets.js`.
 *
 * ## Storage contract (`user.dashboardConfig`)
 *
 * The blob is shared by every dashboard surface of the account, so the phone
 * writes **one dedicated namespace** and never touches the others:
 *
 * ```
 * mobileWorkspaces : { version, activeId, boards:[{id,name,icon,kind,period,templateId,createdAt,widgets:[...]}], updatedAt }
 * mobileManagement : legacy classic Manager layout   (never written by this module)
 * mainBoards / main / main_<id> / gastro / trade / service : admin web layouts (never touched)
 * ```
 *
 * The backend replaces a namespace wholesale and preserves the rest
 * (`mergeDashboardConfig`), so the app and the admin can never wipe each other.
 * One namespace (instead of admin's index + one-per-board) keeps load and save to
 * a single request and makes duplicate / reorder atomic.
 *
 * ## Widget instance
 *
 * `{id, type, size: 'S'|'M'|'L', options?: {period?, restaurantId?, limit?}}`.
 * `type` is a catalogue id (`config/workspaceCatalog`). An unknown `type` is
 * **kept** (never dropped on read or write) - the renderer shows a quiet
 * "open in admin" card, so a layout written by a newer build or by the admin
 * keeps its widgets.
 *
 * ## Workspace kinds
 *
 * `widgets` - a board of catalogue widgets.
 * `classic` - the pre-existing Manager dashboard, rendered as-is. The migration
 * turns the user's current layout into the FIRST workspace by creating exactly
 * one `classic` board: its panels, KPI tiles and quick actions (stored in
 * `mobileManagement`, untouched) stay, and widget workspaces sit next to it.
 *
 * @module helpers/workspaces/workspaceModel
 */

import {DEFAULT_SIZE_BY_KIND, WIDGET_SIZES, resolveWidgetDefinition} from '../../config/workspaceCatalog';

/** `user.dashboardConfig` namespace this surface owns. */
export const WORKSPACES_NAMESPACE = 'mobileWorkspaces';

/** Namespace of the legacy Manager dashboard layout (read-only here). */
export const LEGACY_NAMESPACE = 'mobileManagement';

/** Schema version of the stored entry. */
export const WORKSPACES_VERSION = 1;

/** Most workspaces a user can have (matches the admin's board cap). */
export const MAX_WORKSPACES = 8;

/** Most widgets one workspace may hold (keeps the entry far below the 64 KB namespace cap). */
export const MAX_WIDGETS = 24;

/** Longest workspace name. */
export const MAX_NAME_LENGTH = 40;

/** Workspace kinds. */
export const BOARD_KINDS = Object.freeze({WIDGETS: 'widgets', CLASSIC: 'classic'});

/** Period every workspace starts on (the backend default, month to date). */
export const DEFAULT_PERIOD = 'mtd';

/** Periods the backend `period` parameter accepts (without `custom`). */
export const WORKSPACE_PERIODS = Object.freeze(['today', 'yesterday', '7d', '30d', 'mtd', 'lastMonth', 'qtd', 'ytd']);

/** Error codes returned by the mutators. */
export const WORKSPACE_ERRORS = Object.freeze({
    MAX: 'max',
    MAX_WIDGETS: 'maxWidgets',
    LAST: 'last',
    NOT_FOUND: 'notFound',
    EMPTY_NAME: 'emptyName',
    UNKNOWN_WIDGET: 'unknownWidget',
});

/** Backend ceiling for one namespace entry is 64 KB; stay well below it. */
export const MAX_ENTRY_BYTES = 48 * 1024;

const ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{0,40}$/;

export const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const safeArray = (value) => (Array.isArray(value) ? value : []);

const iso = (now) => (now instanceof Date ? now : new Date()).toISOString();

/**
 * Trim, collapse whitespace and clamp a user supplied name.
 *
 * @param {*} name Raw name.
 * @returns {string} Possibly empty string.
 */
export const sanitizeName = (name) => String(name === null || name === undefined ? '' : name)
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, MAX_NAME_LENGTH);

/**
 * Generate an unused id (`<prefix><6 base36 chars>`).
 *
 * @param {string[]} [existing] Ids already taken.
 * @param {string} [prefix='b'] Leading letter(s) - ids must start with a letter.
 * @param {() => number} [random] Injectable RNG (tests).
 * @returns {string} A collision-free id.
 */
export const newId = (existing = [], prefix = 'b', random = Math.random) => {
    const taken = new Set(existing);
    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';

    for (let attempt = 0; attempt < 50; attempt += 1) {
        let id = prefix;

        for (let i = 0; i < 6; i += 1) {
            id += alphabet[Math.floor(random() * alphabet.length) % alphabet.length];
        }

        if (!taken.has(id)) {
            return id;
        }
    }

    // Practically unreachable; the size suffix keeps two fallbacks in the same millisecond distinct.
    return `${prefix}${Date.now().toString(36)}${taken.size.toString(36)}`;
};

/**
 * Clamp a size to S / M / L.
 *
 * @param {*} size Candidate size.
 * @param {string} [kind] Renderer kind used for the fallback.
 * @returns {'S'|'M'|'L'} A valid size.
 */
export const normalizeSize = (size, kind) => (WIDGET_SIZES.includes(size) ? size : (DEFAULT_SIZE_BY_KIND[kind] || 'M'));

/**
 * Clean an options bag: keep only the keys the renderers read, drop empties.
 *
 * @param {*} options Raw options.
 * @returns {object|undefined} Normalized options, `undefined` when empty.
 */
export const normalizeOptions = (options) => {
    if (!isPlainObject(options)) {
        return undefined;
    }

    const next = {};

    if (WORKSPACE_PERIODS.includes(options.period)) {
        next.period = options.period;
    }

    if (typeof options.restaurantId === 'string' && options.restaurantId) {
        next.restaurantId = options.restaurantId;
    }

    const limit = Number(options.limit);

    if (Number.isFinite(limit) && limit >= 1 && limit <= 20) {
        next.limit = Math.round(limit);
    }

    return Object.keys(next).length ? next : undefined;
};

/**
 * Normalize one stored widget instance. Unknown `type`s are KEPT.
 *
 * @param {*} raw Stored instance.
 * @returns {{id: string, type: string, size: string, options?: object}|null} Instance, `null` when unusable.
 */
export const normalizeWidget = (raw) => {
    if (!isPlainObject(raw) || typeof raw.type !== 'string' || !raw.type || typeof raw.id !== 'string' || !raw.id) {
        return null;
    }

    const definition = resolveWidgetDefinition(raw.type);
    const options = normalizeOptions(raw.options);

    return {
        id: raw.id,
        type: raw.type,
        size: normalizeSize(raw.size, definition.kind),
        ...(options ? {options} : {}),
    };
};

/**
 * Normalize the widgets of a board: drop unusable entries and duplicate ids, cap the count.
 *
 * @param {*} raw Stored widgets.
 * @returns {Array<object>} Clean widget list.
 */
export const normalizeWidgets = (raw) => {
    const seen = new Set();
    const widgets = [];

    safeArray(raw).forEach((item) => {
        const widget = normalizeWidget(item);

        if (!widget || seen.has(widget.id) || widgets.length >= MAX_WIDGETS) {
            return;
        }

        seen.add(widget.id);
        widgets.push(widget);
    });

    return widgets;
};

/**
 * Normalize one stored workspace.
 *
 * @param {*} raw Stored board.
 * @returns {object|null} Board, `null` when it has no usable id.
 */
export const normalizeBoard = (raw) => {
    if (!isPlainObject(raw) || typeof raw.id !== 'string' || !ID_PATTERN.test(raw.id)) {
        return null;
    }

    const kind = raw.kind === BOARD_KINDS.CLASSIC ? BOARD_KINDS.CLASSIC : BOARD_KINDS.WIDGETS;

    return {
        id: raw.id,
        name: sanitizeName(raw.name) || raw.id,
        icon: typeof raw.icon === 'string' ? raw.icon : null,
        kind,
        period: WORKSPACE_PERIODS.includes(raw.period) ? raw.period : DEFAULT_PERIOD,
        templateId: typeof raw.templateId === 'string' ? raw.templateId : null,
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : null,
        widgets: kind === BOARD_KINDS.CLASSIC ? [] : normalizeWidgets(raw.widgets),
    };
};

/**
 * Normalize a raw stored state (read path). Invalid boards are dropped, duplicate
 * ids removed, the list is capped and `activeId` repaired.
 *
 * @param {*} raw `dashboardConfig.mobileWorkspaces`.
 * @returns {object|null} State, `null` when nothing usable is stored.
 */
export const normalizeState = (raw) => {
    if (!isPlainObject(raw) || !Array.isArray(raw.boards)) {
        return null;
    }

    const seen = new Set();
    const boards = [];

    raw.boards.forEach((item) => {
        const board = normalizeBoard(item);

        if (!board || seen.has(board.id) || boards.length >= MAX_WORKSPACES) {
            return;
        }

        seen.add(board.id);
        boards.push(board);
    });

    if (!boards.length) {
        return null;
    }

    return {
        version: WORKSPACES_VERSION,
        activeId: boards.some((board) => board.id === raw.activeId) ? raw.activeId : boards[0].id,
        boards,
        updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
    };
};

/**
 * Stable on-the-wire shape (write path): the normalized state stamped with `updatedAt`.
 *
 * @param {object} state Workspace state.
 * @param {Date} [now] Clock (tests).
 * @returns {object} Serializable entry for `dashboardConfig.mobileWorkspaces`.
 */
export const serializeState = (state, now) => ({
    ...(normalizeState(state) || {version: WORKSPACES_VERSION, activeId: null, boards: []}),
    updatedAt: iso(now),
});

/**
 * Approximate serialized size of the entry (UTF-8 bytes).
 *
 * @param {object} state Workspace state.
 * @returns {number} Byte length of the JSON.
 */
export const estimateBytes = (state) => {
    const json = JSON.stringify(state || {});
    let bytes = 0;

    for (let i = 0; i < json.length; i += 1) {
        const code = json.charCodeAt(i);

        bytes += code < 0x80 ? 1 : (code < 0x800 ? 2 : 3);
    }

    return bytes;
};

/**
 * Whether the entry still fits the backend's per-namespace cap.
 *
 * @param {object} state Workspace state.
 * @returns {boolean} True when it can be saved.
 */
export const fitsBudget = (state) => estimateBytes(state) <= MAX_ENTRY_BYTES;

// ---------------------------------------------------------------------------
// Construction & migration
// ---------------------------------------------------------------------------

const makeBoard = (input, existingIds, now, random) => ({
    id: input.id && ID_PATTERN.test(input.id) && !existingIds.includes(input.id) ? input.id : newId(existingIds, 'b', random),
    name: sanitizeName(input.name),
    icon: input.icon || null,
    kind: input.kind === BOARD_KINDS.CLASSIC ? BOARD_KINDS.CLASSIC : BOARD_KINDS.WIDGETS,
    period: WORKSPACE_PERIODS.includes(input.period) ? input.period : DEFAULT_PERIOD,
    templateId: input.templateId || null,
    createdAt: iso(now),
    widgets: input.kind === BOARD_KINDS.CLASSIC ? [] : normalizeWidgets(input.widgets),
});

/**
 * MIGRATION: turn the user's current Manager dashboard into the first workspace.
 *
 * The existing layout (`dashboardConfig.mobileManagement`: KPI tiles, panels,
 * quick actions, column density) is rendered by the unchanged Manager dashboard,
 * so the migration does not re-encode it - it creates ONE `classic` workspace
 * that stands for it. Nothing in the legacy namespace is read, written or
 * deleted, so reverting is lossless and the old customize screen keeps working
 * inside that workspace.
 *
 * @param {object} [options] Options.
 * @param {string} [options.name] Localized name of the first workspace.
 * @param {Date} [options.now] Clock.
 * @returns {object} A one-workspace state.
 */
export const createInitialState = ({name, now} = {}) => {
    const board = {
        id: 'overview',
        name: sanitizeName(name) || 'Overview',
        icon: 'view-dashboard-outline',
        kind: BOARD_KINDS.CLASSIC,
        period: DEFAULT_PERIOD,
        templateId: null,
        createdAt: iso(now),
        widgets: [],
    };

    return {version: WORKSPACES_VERSION, activeId: board.id, boards: [board], updatedAt: null};
};

/**
 * Resolve the state to start from: the stored one, or the migration of the
 * legacy layout when the user has never used workspaces.
 *
 * @param {*} storedEntry `dashboardConfig.mobileWorkspaces` (may be absent).
 * @param {object} [options] Options for {@link createInitialState}.
 * @returns {{state: object, migrated: boolean}} State and whether it was freshly migrated.
 */
export const resolveInitialState = (storedEntry, options = {}) => {
    const stored = normalizeState(storedEntry);

    return stored ? {state: stored, migrated: false} : {state: createInitialState(options), migrated: true};
};

// ---------------------------------------------------------------------------
// Workspace (tab) operations - every function returns {state, error, ...}
// ---------------------------------------------------------------------------

const findIndex = (state, boardId) => state.boards.findIndex((board) => board.id === boardId);

const fail = (state, error) => ({state, error});

const withBoards = (state, boards, patch = {}) => ({...state, ...patch, boards});

/**
 * Add a workspace (and make it active).
 *
 * @param {object} state Current state.
 * @param {object} input Board fields: `name`, `id?`, `kind?`, `widgets?`, `icon?`, `templateId?`, `period?`.
 * @param {{now?: Date, random?: Function}} [env] Injectables.
 * @returns {{state: object, board: ?object, error: ?string}} Result.
 */
export const addBoard = (state, input, {now, random} = {}) => {
    if (state.boards.length >= MAX_WORKSPACES) {
        return {...fail(state, WORKSPACE_ERRORS.MAX), board: null};
    }

    if (!sanitizeName(input?.name)) {
        return {...fail(state, WORKSPACE_ERRORS.EMPTY_NAME), board: null};
    }

    const board = makeBoard(input, state.boards.map((item) => item.id), now, random);

    return {state: withBoards(state, [...state.boards, board], {activeId: board.id}), board, error: null};
};

/**
 * Rename a workspace.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} name New name.
 * @returns {{state: object, error: ?string}} Result.
 */
export const renameBoard = (state, boardId, name) => {
    const index = findIndex(state, boardId);
    const clean = sanitizeName(name);

    if (index === -1) {
        return fail(state, WORKSPACE_ERRORS.NOT_FOUND);
    }

    if (!clean) {
        return fail(state, WORKSPACE_ERRORS.EMPTY_NAME);
    }

    if (state.boards[index].name === clean) {
        return {state, error: null};
    }

    return {
        state: withBoards(state, state.boards.map((board) => (board.id === boardId ? {...board, name: clean} : board))),
        error: null,
    };
};

/**
 * Delete a workspace. The last one can never be deleted.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @returns {{state: object, removed: ?object, error: ?string}} Result.
 */
export const removeBoard = (state, boardId) => {
    const removed = state.boards.find((board) => board.id === boardId) || null;

    if (!removed) {
        return {...fail(state, WORKSPACE_ERRORS.NOT_FOUND), removed: null};
    }

    if (state.boards.length <= 1) {
        return {...fail(state, WORKSPACE_ERRORS.LAST), removed: null};
    }

    const index = findIndex(state, boardId);
    const boards = state.boards.filter((board) => board.id !== boardId);
    // Landing on the previous tab (or the new first one) feels natural.
    const activeId = state.activeId === boardId ? boards[Math.max(0, index - 1)].id : state.activeId;

    return {state: withBoards(state, boards, {activeId}), removed, error: null};
};

/**
 * Duplicate a workspace (fresh widget ids so two boards never share an instance id).
 *
 * @param {object} state Current state.
 * @param {string} boardId Board to copy.
 * @param {{name?: string, now?: Date, random?: Function}} [env] Copy name and injectables.
 * @returns {{state: object, board: ?object, error: ?string}} Result (the copy becomes active, right after the source).
 */
export const duplicateBoard = (state, boardId, {name, now, random} = {}) => {
    const source = state.boards.find((board) => board.id === boardId);

    if (!source) {
        return {...fail(state, WORKSPACE_ERRORS.NOT_FOUND), board: null};
    }

    if (state.boards.length >= MAX_WORKSPACES) {
        return {...fail(state, WORKSPACE_ERRORS.MAX), board: null};
    }

    const widgetIds = [];
    const widgets = source.widgets.map((widget) => {
        const id = newId(widgetIds, 'w', random);

        widgetIds.push(id);

        return {...widget, id, ...(widget.options ? {options: {...widget.options}} : {})};
    });
    const copy = makeBoard({
        name: name || `${source.name} (2)`,
        kind: source.kind,
        icon: source.icon,
        period: source.period,
        templateId: source.templateId,
        widgets,
    }, state.boards.map((board) => board.id), now, random);
    const at = findIndex(state, boardId) + 1;

    return {
        state: withBoards(state, [...state.boards.slice(0, at), copy, ...state.boards.slice(at)], {activeId: copy.id}),
        board: copy,
        error: null,
    };
};

/**
 * Move a workspace tab from one position to another.
 *
 * @param {object} state Current state.
 * @param {number} from Source index.
 * @param {number} to Target index.
 * @returns {object} New state (same reference when nothing moved).
 */
export const moveBoard = (state, from, to) => {
    const size = state.boards.length;

    if (from === to || from < 0 || to < 0 || from >= size || to >= size) {
        return state;
    }

    const boards = [...state.boards];
    const [moved] = boards.splice(from, 1);

    boards.splice(to, 0, moved);

    return withBoards(state, boards);
};

/**
 * Move a workspace one step left (-1) or right (+1).
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {number} direction -1 | +1.
 * @returns {object} New state (same reference at the edge).
 */
export const moveBoardBy = (state, boardId, direction) => {
    const from = findIndex(state, boardId);

    return from === -1 ? state : moveBoard(state, from, from + (direction < 0 ? -1 : 1));
};

/**
 * Switch the active workspace.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @returns {object} New state (same reference when unknown or already active).
 */
export const setActiveBoard = (state, boardId) => (
    state.activeId !== boardId && findIndex(state, boardId) !== -1 ? {...state, activeId: boardId} : state
);

/**
 * Set the period a workspace's widgets default to.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} period One of {@link WORKSPACE_PERIODS}.
 * @returns {object} New state (same reference when invalid / unchanged).
 */
export const setBoardPeriod = (state, boardId, period) => {
    const board = state.boards.find((item) => item.id === boardId);

    if (!board || !WORKSPACE_PERIODS.includes(period) || board.period === period) {
        return state;
    }

    return withBoards(state, state.boards.map((item) => (item.id === boardId ? {...item, period} : item)));
};

/**
 * Apply a transform to one board; returns the same state when the transform is a no-op.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {(board: object) => object} transform Pure board transform (return the same reference for no-op).
 * @returns {object} New state.
 */
export const updateBoard = (state, boardId, transform) => {
    const board = state.boards.find((item) => item.id === boardId);

    if (!board) {
        return state;
    }

    const next = transform(board);

    return next === board ? state : withBoards(state, state.boards.map((item) => (item.id === boardId ? next : item)));
};

/**
 * Whether a board renders catalogue widgets (i.e. is not the classic dashboard).
 *
 * @param {object} board Board.
 * @returns {boolean} True for a widget board.
 */
export const isWidgetBoard = (board) => !!board && board.kind !== BOARD_KINDS.CLASSIC;

// ---------------------------------------------------------------------------
// Per-namespace merge (client-side mirror of the backend rule)
// ---------------------------------------------------------------------------

/**
 * Build the PATCH sent to the account: ONLY this surface's namespace.
 *
 * @param {object} state Workspace state.
 * @param {Date} [now] Clock.
 * @returns {Object.<string, object>} `{mobileWorkspaces: entry}`.
 */
export const buildSavePatch = (state, now) => ({[WORKSPACES_NAMESPACE]: serializeState(state, now)});

/**
 * Merge a patch into a `dashboardConfig` blob PER NAMESPACE - the backend's
 * `mergeDashboardConfig` rule, mirrored so the local mirror and the optimistic
 * state never disagree with the server:
 *  - a namespace in the patch replaces the stored one wholesale;
 *  - a namespace absent from the patch is preserved untouched;
 *  - `null` removes a namespace.
 *
 * Never mutates its inputs.
 *
 * @param {object|null|undefined} existing Stored blob.
 * @param {object|null|undefined} patch Namespaces to write.
 * @returns {object} The merged blob.
 */
export const mergeDashboardNamespaces = (existing, patch) => {
    const base = isPlainObject(existing) ? {...existing} : {};

    if (!isPlainObject(patch)) {
        return base;
    }

    Object.keys(patch).forEach((namespace) => {
        if (patch[namespace] === null || patch[namespace] === undefined) {
            delete base[namespace];
        } else {
            base[namespace] = patch[namespace];
        }
    });

    return base;
};

/**
 * Pick the newer of the local mirror and the account copy (ISO `updatedAt`).
 * A copy without a stamp loses to one with a stamp; a tie prefers the local
 * (it may hold an edit still being debounced).
 *
 * @param {?object} local Local state.
 * @param {?object} remote Remote state.
 * @returns {?object} The state to adopt.
 */
export const pickNewer = (local, remote) => {
    if (!local) {
        return remote || null;
    }

    if (!remote) {
        return local;
    }

    const left = local.updatedAt ? Date.parse(local.updatedAt) : 0;
    const right = remote.updatedAt ? Date.parse(remote.updatedAt) : 0;

    return right > left ? remote : local;
};
